import { put, select, takeLatest } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import { fetchListFailure, fetchListRequest, fetchListSuccess } from './WebsalesUsersManagementSlice'
import { WebsalesUsersRoutes } from './types'

export const websalesUsersManagementActions = {
  LOAD_LIST: 'WEBSALES_USERS_MANAGEMENT/LOAD_LIST',
  CREATE_WEBSALES_USER: 'WEBSALES_USERS_MANAGEMENT/CREATE',
  UPDATE_WEBSALES_USER: 'WEBSALES_USERS_MANAGEMENT/UPDATE',
  DELETE_WEBSALES_USER: 'WEBSALES_USERS_MANAGEMENT/DELETE',
  REGENERATE_WEBSALES_USER_PASSWORD: 'WEBSALES_USERS_MANAGEMENT/REGENERATE_PASSWORD',
}

const getWebsalesUsersRoutes = (authData: any): WebsalesUsersRoutes => authData?.extraRoutes?.websalesUsers ?? {}

export function* loadWebsalesUsersSaga(action: any = {}): Generator {
  const { enqueue } = action
  try {
    yield put(fetchListRequest())
    const authData: any = yield select(selectAuthData)
    const routes = getWebsalesUsersRoutes(authData)
    if (!routes.list) return

    const data: any = yield callApi({ endpoint: routes.list, enqueue })
    yield put(fetchListSuccess(Array.isArray(data?.data) ? data.data : []))
  } catch (error) {
    yield put(fetchListFailure(error instanceof Error ? error.message : 'Unknown error'))
  }
}

export function* createWebsalesUser(action: any): Generator {
  const { websalesUserData, enqueue, onSuccess, setFormErrors } = action
  try {
    const authData: any = yield select(selectAuthData)
    const routes = getWebsalesUsersRoutes(authData)
    if (!routes.create) return

    const data: any = yield callApi({
      endpoint: routes.create,
      method: 'POST',
      payload: websalesUserData,
      enqueue,
      showErrorSnackbar: false,
    })

    onSuccess(data?.message)
    yield* loadWebsalesUsersSaga()
  } catch (error: any) {
    if (error?.validationErrors && Object.keys(error.validationErrors).length > 0) {
      setFormErrors(error.validationErrors)
    } else if (enqueue) {
      showSnackbar(enqueue, { type: SNACKBAR_TYPES.ERROR, message: error?.message })
    }
  }
}

export function* updateWebsalesUser(action: any): Generator {
  const { websalesUserData, enqueue, onSuccess, setFormErrors } = action
  try {
    const authData: any = yield select(selectAuthData)
    const routes = getWebsalesUsersRoutes(authData)
    if (!routes.update) return

    const data: any = yield callApi({
      endpoint: routes.update,
      method: 'PUT',
      payload: websalesUserData,
      asteriskReplacement: websalesUserData.id,
      enqueue,
      showErrorSnackbar: false,
    })

    onSuccess(data?.message)
    yield* loadWebsalesUsersSaga()
  } catch (error: any) {
    if (error?.validationErrors && Object.keys(error.validationErrors).length > 0) {
      setFormErrors(error.validationErrors)
    } else if (enqueue) {
      showSnackbar(enqueue, { type: SNACKBAR_TYPES.ERROR, message: error?.message })
    }
  }
}

export function* deleteWebsalesUser(action: any): Generator {
  const { websalesUserId, enqueue } = action
  try {
    const authData: any = yield select(selectAuthData)
    const routes = getWebsalesUsersRoutes(authData)
    if (!routes.delete) return

    yield callApi({
      endpoint: routes.delete,
      method: 'DELETE',
      asteriskReplacement: websalesUserId,
      enqueue,
    })
    yield* loadWebsalesUsersSaga()
  } catch {
    // callApi already notified the error via the snackbar
  }
}

export function* regenerateWebsalesUserPassword(action: any): Generator {
  const { websalesUserId, enqueue, onSuccess, onError } = action
  try {
    const authData: any = yield select(selectAuthData)
    const routes = getWebsalesUsersRoutes(authData)
    if (!routes.regeneratePassword) return

    const data: any = yield callApi({
      endpoint: routes.regeneratePassword,
      method: 'POST',
      asteriskReplacement: websalesUserId,
      enqueue,
    })

    const password = data?.data?.password
    if (password) {
      onSuccess(password)
    } else if (onError) {
      onError()
    }
  } catch {
    if (onError) onError()
  }
}

export default function* websalesUsersManagementSagas() {
  yield takeLatest(websalesUsersManagementActions.LOAD_LIST, loadWebsalesUsersSaga)
  yield takeLatest(websalesUsersManagementActions.CREATE_WEBSALES_USER, createWebsalesUser)
  yield takeLatest(websalesUsersManagementActions.UPDATE_WEBSALES_USER, updateWebsalesUser)
  yield takeLatest(websalesUsersManagementActions.DELETE_WEBSALES_USER, deleteWebsalesUser)
  yield takeLatest(
    websalesUsersManagementActions.REGENERATE_WEBSALES_USER_PASSWORD,
    regenerateWebsalesUserPassword,
  )
}
